import React, { useState, useEffect } from 'react';
import { motion, Variants } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const images = [
    '/ambani/1.JPG',
    '/ambani/2.JPG',
    '/ambani/3.JPG',
    '/ambani/4.JPG'
  ];

  // Auto-advance slider every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  return (
    <section
      id="top"
      className="min-h-screen h-screen w-full flex flex-col items-center justify-center relative overflow-hidden pt-20 sm:pt-0"
    >
      {/* Background Image Slider */}
      <div className="absolute inset-0 w-full h-full">
        {images.map((img, index) => (
          <motion.img
            key={img}
            src={img}
            alt={`Slide ${index + 1}`}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: currentSlide === index ? 1 : 0 }}
            transition={{ duration: 1 }}
          />
        ))}
      </div>

      <div className="absolute inset-0 bg-black/55" />

      {/* Left Slider Dots */}
      <motion.div
        initial={{ opacity: 0, x: -15 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1 }}
        className="hidden lg:flex flex-col gap-3 absolute left-10 top-1/2 -translate-y-1/2 z-20"
      >
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full border border-white transition-all duration-300 ${currentSlide === index ? 'bg-white' : 'bg-transparent'
              }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </motion.div>



      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-20 text-center max-w-4xl mx-auto px-4 sm:px-6"
      >
        <motion.p
          variants={itemVariants}
          className="text-white/70 text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-6 sm:mb-8"
        >
          Celebrating Love Since 2000
        </motion.p>

        <motion.h1
          variants={itemVariants}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-tight mb-8 sm:mb-10"
        >
          Turning Your Vows<br />
          into a Celebration of a Lifetime
        </motion.h1>

        <motion.div
          variants={itemVariants}
          className="flex flex-col items-center justify-center gap-6"
        >
          {/* Primary CTA with decorative lines */}
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
            className="relative px-8 sm:px-10 py-3 sm:py-4 border border-white/70 text-white text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] uppercase bg-transparent hover:bg-white/10 transition-colors"
          >
            {/* Ornament above button - hidden on very small screens */}
            <span className="pointer-events-none absolute -top-3 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-2 text-white">
              <span className="block h-px w-10 bg-white/60" />
              <span className="block w-2 h-2 rounded-full border border-white/70" />
              <span className="block h-px w-10 bg-white/60" />
            </span>
            BEGIN MY WEDDING STORY
          </motion.button>

          {/* Subtext link under button */}
          <motion.p className="text-white/70 text-[10px] tracking-[0.4em] uppercase">
            WHY COUPLES TRUST US
          </motion.p>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        variants={itemVariants}
        className="absolute bottom-10 inset-x-0 mx-auto w-fit z-20 text-center text-white"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity }}
      >
        <p className="text-[11px] font-light tracking-[0.4em] uppercase mb-4">Scroll Down</p>
        <ChevronDown className="w-5 h-5 mx-auto" />
      </motion.div>
    </section>
  );
}